import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useOnboardingCompany } from '@/hooks/useOnboardingCompany';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, Check, X, Loader2 } from 'lucide-react';
import { PageSeo } from '@/components/seo/PageSeo';
import { NOINDEX_FOLLOW } from '@/lib/seo/constants';
import { onboardingSeo } from '@/lib/seo/authSeo';

/** Mirrors BE CreateCompanySchema (RecruiterAI-BE/src/shared/dtos/company.dto.ts). */
const COMPANY_NAME_MIN = 2;
const COMPANY_PUBLIC_NAME_MIN = 1;
const COMPANY_DESCRIPTION_MIN = 10;
const COMPANY_DESCRIPTION_MAX = 5000;

const OnboardingPage = () => {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const skipCompany = searchParams.get('skipCompany') === 'true';
  const fromSignup = searchParams.get('fromSignup') === 'true';
  const redirectTo = searchParams.get('redirect') ?? '/dashboard';
  const { user, setSelectedCompany, refreshSession, isLoading: authLoading } = useAuth();

  // Auto-skip company creation when:
  //   A) ?skipCompany=true — explicit param from invite-link signup flow
  //      (user signed up via /signup?redirect=/i/:id/:token)
  //   B) fromSignup=true AND user already has memberships — backend auto-accepted
  //      a pending invitation during POST /auth/session (user signed up directly
  //      without preserving the invite redirect, Scenario B)
  //
  // Do NOT auto-skip based on memberships alone — the user may intentionally
  // navigate here via "+ Add Company" to create an additional company.
  const hasMembership =
    !!user && !!user.memberships && Object.keys(user.memberships).length > 0;

  const shouldSkipCompany = skipCompany || (fromSignup && hasMembership);
  /** Signup redirect can mount before `/auth/session` resolves — avoid flashing step 1. */
  const awaitingSignupAuth = fromSignup && authLoading;

  const [step, setStep] = useState(shouldSkipCompany ? 3 : 1);
  const [companyName, setCompanyName] = useState('');
  const [publicName, setPublicName] = useState('');
  const [description, setDescription] = useState('');
  const [skippedCompany, setSkippedCompany] = useState(shouldSkipCompany);
  const { createCompany, saving, error } = useOnboardingCompany();
  const navigate = useNavigate();

  // Jump to step 3 when skipCompany param is set. Runs when user context resolves.
  useEffect(() => {
    if (shouldSkipCompany) {
      setSkippedCompany(true);
      setStep(3);
    }
  }, [shouldSkipCompany]);

  const handleSkipCompany = () => {
    setSkippedCompany(true);
    setStep(3);
  };

  // Client-side mirror of BE CreateCompanySchema validation.
  // Surfaces friendly error messages BEFORE we hit the API.
  const trimmedName = companyName.trim();
  const trimmedPublicName = (publicName || companyName).trim();
  const trimmedDescription = description.trim();
  const validationErrors: string[] = [];
  if (trimmedName.length < COMPANY_NAME_MIN) {
    validationErrors.push(`Company Name must be at least ${COMPANY_NAME_MIN} characters.`);
  }
  if (trimmedPublicName.length < COMPANY_PUBLIC_NAME_MIN) {
    validationErrors.push('Public Name is required.');
  }
  if (trimmedDescription.length < COMPANY_DESCRIPTION_MIN) {
    validationErrors.push(
      `Description must be at least ${COMPANY_DESCRIPTION_MIN} characters (${trimmedDescription.length} so far).`,
    );
  }
  if (trimmedDescription.length > COMPANY_DESCRIPTION_MAX) {
    validationErrors.push(`Description must be at most ${COMPANY_DESCRIPTION_MAX} characters.`);
  }
  const isStep1Valid = validationErrors.length === 0;

  /**
   * Step 1 → Step 2: validate AND persist the company up-front.
   * UX rationale: previously the API call only fired at the final "Go to Dashboard"
   * click on step 3, so a 400 from the BE was shown after a fake "You're all set!"
   * success screen. Creating eagerly means failures surface where the user can fix them.
   *
   * After successful creation we ALSO refresh the auth session — the new
   * membership is created server-side in the same transaction as the company,
   * but `user.memberships` on the client is the snapshot from the previous
   * `/auth/session` sync. Without `refreshSession()` the user sees zero
   * memberships in their Profile page even though the membership exists in DB.
   */
  const handleStep1Continue = async () => {
    if (!isStep1Valid) return;
    try {
      const company = await createCompany({
        name: trimmedName,
        publicName: trimmedPublicName,
        description: trimmedDescription,
      });
      setSelectedCompany(company);
      // Best-effort: do not block step navigation if refresh fails.
      void refreshSession().catch(() => {});
      // SME default accounts have a single user — skip the "Invite your team"
      // step (was step 2) and go straight to the success screen. Users can
      // invite team members later from Settings → Members.
      setStep(3);
    } catch {
      // hook sets `error`; we stay on step 1 so the user can fix the body.
    }
  };

  const handleFinish = () => {
    // Company is already created by the time we reach step 3 (or skipped onboarding).
    navigate(redirectTo, { replace: true });
  };

  const seo = onboardingSeo(language);

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/30 px-4 relative">
      <PageSeo title={seo.title} path="/onboarding" robots={NOINDEX_FOLLOW} />
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
        onClick={() => navigate(-1)}
      >
        <X className="h-5 w-5" />
      </Button>
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <svg viewBox="0 0 360 320" xmlns="http://www.w3.org/2000/svg" className="h-9 w-9" aria-label="HiroMatch">
            <defs>
              <clipPath id="hm-onb-clip"><circle cx="225" cy="160" r="90" /></clipPath>
            </defs>
            <circle cx="135" cy="160" r="88" fill="#7C3AED" opacity="0.9" />
            <circle cx="225" cy="160" r="90" fill="#C4B5FD" opacity="0.82" />
            <circle cx="135" cy="160" r="88" fill="#9333EA" opacity="0.3" clipPath="url(#hm-onb-clip)" />
          </svg>
          <span className="text-xl font-semibold tracking-tight font-display">HiroMatch</span>
        </div>

        {!skippedCompany && step !== 3 && !awaitingSignupAuth && (
          <div className="flex items-center justify-center gap-2 mb-6">
            {/* Two-step flow: 1 = create company, 2 = success. Internal step==3
                maps to display step 2 (we kept the internal id to avoid
                renumbering after removing the deprecated invite step). */}
            {[1, 3].map(s => (
              <div key={s} className={`h-2 rounded-full transition-all ${s === step ? 'w-8 bg-primary' : s < step ? 'w-8 bg-primary/40' : 'w-8 bg-border'}`} />
            ))}
          </div>
        )}

        <Card className="border-border/50 shadow-sm">
          {awaitingSignupAuth && (
            <CardContent className="py-12 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label={t('common.loading')} />
            </CardContent>
          )}
          {!awaitingSignupAuth && step === 1 && (
            <>
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-lg">{t('onboarding.title')}</CardTitle>
                <CardDescription>{t('onboarding.subtitle')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="onb-name">{t('onboarding.companyName')}</Label>
                  <Input
                    id="onb-name"
                    value={companyName}
                    onChange={e => setCompanyName(e.target.value)}
                    placeholder="Acme Inc."
                    aria-invalid={trimmedName.length > 0 && trimmedName.length < COMPANY_NAME_MIN}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="onb-publicName">{t('onboarding.publicName')}</Label>
                  <Input
                    id="onb-publicName"
                    value={publicName}
                    onChange={e => setPublicName(e.target.value)}
                    placeholder="Acme"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('onboarding.publicNameHint')}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="onb-description">
                    {t('onboarding.description')}{' '}
                    <span className="text-xs text-muted-foreground font-normal">
                      ({t('onboarding.descriptionHint').replace('{min}', String(COMPANY_DESCRIPTION_MIN))} · {trimmedDescription.length}/{COMPANY_DESCRIPTION_MAX})
                    </span>
                  </Label>
                  <Textarea
                    id="onb-description"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="What does your company do? Industry, mission, team size…"
                    rows={3}
                    aria-invalid={trimmedDescription.length > 0 && trimmedDescription.length < COMPANY_DESCRIPTION_MIN}
                  />
                </div>
                {error && (
                  <p className="text-sm text-destructive" role="alert">
                    {error}
                  </p>
                )}
                {!isStep1Valid && (companyName || description) && (
                  <ul className="text-xs text-amber-700 space-y-0.5" role="status">
                    {validationErrors.map(msg => (
                      <li key={msg}>· {msg}</li>
                    ))}
                  </ul>
                )}
                <Button
                  className="w-full gap-2"
                  onClick={handleStep1Continue}
                  disabled={!isStep1Valid || saving}
                >
                  {saving ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> {t('onboarding.creating')}</>
                  ) : (
                    <>{t('onboarding.continue')} <ArrowRight className="h-4 w-4" /></>
                  )}
                </Button>
                <Button type="button" variant="outline" className="w-full text-sm" onClick={handleSkipCompany}>
                  {t('onboarding.joinExisting')}
                </Button>
              </CardContent>
            </>
          )}
          {/* Step 2 ("Invite your team") removed — SME default accounts ship
              with a single seat and team invitations are managed later from
              Settings → Members. We jump directly from step 1 (create company)
              to step 3 (success). */}
          {!awaitingSignupAuth && step === 3 && (
            <>
              <CardHeader className="text-center pb-4">
                <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Check className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">{t('onboarding.successTitle')}</CardTitle>
                <CardDescription>
                  {skippedCompany
                    ? t('onboarding.successDescSkipped')
                    : t('onboarding.successDescCreated')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button className="w-full" onClick={handleFinish}>
                  {t('onboarding.goToDashboard')}
                </Button>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default OnboardingPage;
